/**
 * 主入口：Input 初始化 → 资源加载（进度条）→ 音频解锁挂首次交互
 * → 注册全部场景 → 进入标题 → 启动固定步长循环。
 */
import { VIEW_W, VIEW_H } from './config.js';
import { Input } from './engine/input.js';
import { Assets } from './engine/assets.js';
import { AudioSys } from './engine/audio.js';
import { Renderer } from './engine/renderer.js';
import { Loop } from './engine/loop.js';
import { SceneManager } from './scenes/sceneManager.js';
import { GameState } from './game/state.js';
import { MANIFEST } from './manifest.js';
import * as BF from './content/stages/bosses.js';
import { TitleScene } from './scenes/title.js';
import { DifficultySelectScene } from './scenes/difficultySelect.js';
import { CharSelectScene } from './scenes/charSelect.js';
import { GameScene } from './scenes/gameScene.js';
import { ResultScene } from './scenes/result.js';
import { LeaderboardScene } from './scenes/leaderboard.js';
import { ReplayListScene } from './scenes/replayList.js';
import { SpellPracticeScene } from './scenes/spellPractice.js';
import { CreditsScene } from './scenes/credits.js';
import { OptionsScene } from './scenes/options.js';
import { MusicRoomScene } from './scenes/musicRoom.js';

const canvas = document.getElementById('game');
const renderer = new Renderer(canvas);
Input.init(window);

// 浏览器自动播放策略：首次用户手势解锁 AudioContext
window.addEventListener('pointerdown', () => AudioSys.ensureCtx(), { once: true });
window.addEventListener('keydown', () => AudioSys.ensureCtx(), { once: true });

function drawProgress(p) {
  renderer.begin();
  renderer.text('KIMI 弹幕大作战', 240, 250, { size: 22, color: '#fff', align: 'center' });
  renderer.text('LOADING…', 240, 290, { size: 14, color: '#8cf', align: 'center' });
  renderer.rect(90, 316, 300, 12, { stroke: '#fff' });
  renderer.rect(92, 318, 296 * p, 8, { fill: '#4af' });
  renderer.text(`${Math.round(p * 100)}%`, 240, 340, { size: 12, color: '#ccc', align: 'center' });
  renderer.end();
}

async function boot() {
  drawProgress(0);
  // 等待内置字体就绪（canvas 文本使用；失败不阻塞，回退 monospace）
  try {
    if (document.fonts && document.fonts.load) {
      await Promise.race([
        document.fonts.load('16px "Fusion Pixel"'),
        new Promise((r) => setTimeout(r, 3000)),
      ]);
    }
  } catch (e) { /* 字体加载失败：回退 monospace */ }
  // 生产构建的资源指纹与打包产物（tools/build.mjs 生成 dist/ 下的表；
  // dev 模块直载时无这些文件，静默用裸路径逐文件加载）
  let assetVer = {};
  let atlas = null;
  let sfxSprite = null;
  try {
    const res = await fetch('dist/asset-manifest.json');
    if (res.ok) assetVer = await res.json();
    const [a, s] = await Promise.all([fetch('dist/atlas.json'), fetch('dist/sfx.json')]);
    if (a.ok) atlas = await a.json();
    if (s.ok) sfxSprite = await s.json();
  } catch (e) { /* dev 模式无指纹表 */ }
  const versioned = (url) => (assetVer[url] ? `${url}?v=${assetVer[url]}` : url);
  const images = {};
  const audios = {};
  for (const [k, u] of Object.entries(MANIFEST.images)) {
    if (atlas && atlas.frames[k]) continue; // 已打进 atlas，不单独请求
    images[k] = versioned(u);
  }
  // BGM 懒加载：启动必载只有 title（主菜单/结算）与 clear（过关曲）+ SFX sprite；
  // 其余 15 首注册 URL 后由后台队列拉取（4 并发按使用顺序），进关/credits 前硬等
  const EAGER_BGM = new Set(['bgm_title', 'bgm_clear']);
  const audioUrls = {};
  for (const [k, u] of Object.entries(MANIFEST.audios)) {
    if (sfxSprite && sfxSprite.clips[k]) continue; // 已并入 SFX sprite
    const url = versioned(u);
    audioUrls[k] = url;
    if (EAGER_BGM.has(k)) audios[k] = url;
  }
  if (atlas) images.__atlas = versioned(`dist/${atlas.image}`);
  if (sfxSprite) audios.__sfxSprite = versioned(`dist/${sfxSprite.file}`);
  Assets.audioUrls = audioUrls;
  await Assets.load({ images, audios }, drawProgress);
  // 图片补漏：首轮加载失败（warn 但不阻塞）的图片重试一次（用户定案：尽量尝试预加载）
  {
    const missing = {};
    for (const [k, u] of Object.entries(images)) {
      if (!Assets.images.has(k)) missing[k] = u;
    }
    if (Object.keys(missing).length) await Assets.load({ images: missing }, drawProgress);
  }

  // atlas 帧包装：img(key) 返回 {__atlas, img, sx, sy, w, h, width, height}（renderer 识别）
  if (atlas) {
    const sheet = Assets.img('__atlas');
    Assets.images.delete('__atlas');
    if (sheet) {
      for (const [k, f] of Object.entries(atlas.frames)) {
        Assets.images.set(k, { __atlas: true, img: sheet, sx: f.x, sy: f.y, w: f.w, h: f.h, width: f.w, height: f.h });
      }
    } else {
      // atlas 本体加载失败（部署中途/CDN 部分失效）：回退逐文件加载，
      // 否则 96 个帧 key 全部指向 undefined 每帧崩渲染（与下方 sfx 的 if(buf) 同款守卫）
      const fallback = {};
      for (const k of Object.keys(atlas.frames)) {
        fallback[k] = versioned(MANIFEST.images[k]);
      }
      await Assets.load({ images: fallback }, drawProgress);
    }
  }
  // SFX 分段包装：audio(key) 返回 {__sprite, buf, offset, duration}（audio.js 识别）
  if (sfxSprite) {
    const buf = Assets.audio('__sfxSprite');
    Assets.audios.delete('__sfxSprite');
    // 无 buf 不包装：sprite 本体加载失败时保持"key 缺失→静默"语义，
    // 否则包装出 buf:undefined 的假 key（全部 SFX 无声的边缘 bug）
    if (buf) {
      for (const [k, c] of Object.entries(sfxSprite.clips)) {
        Assets.audios.set(k, { __sprite: true, buf, offset: c.offset, duration: c.duration });
      }
    }
  }

  const mgr = new SceneManager();
  const state = new GameState();
  try {
    state.hiscore = Number(globalThis.localStorage?.getItem('kimi-hiscore') || 0);
  } catch (e) { /* 忽略 */ }

  // FPS 显示开关（F 键）：按 rAF 实际帧率统计
  let showFps = false;
  let fps = 0;
  let fpsFrames = 0;
  let fpsLast = performance.now();

  const loop = new Loop({
    update(dt) {
      mgr.update(dt);
      if (Input.pressed('fps')) {
        showFps = !showFps;
        // 开启时重置统计窗：否则首个窗口分母是"关闭至今"的整段墙钟，瞬态显示 0 FPS（历史 bug）
        if (showFps) { fpsFrames = 0; fpsLast = performance.now(); }
      }
      Input.update();
    },
    render() {
      renderer.begin();
      mgr.draw(renderer);
      if (showFps) {
        fpsFrames++;
        const now = performance.now();
        if (now - fpsLast >= 500) {
          fps = Math.round((fpsFrames * 1000) / (now - fpsLast));
          fpsFrames = 0;
          fpsLast = now;
        }
        renderer.text(`${fps} FPS`, VIEW_W - 8, 6, { size: 10, color: fps >= 50 ? '#8f8' : '#f88', align: 'right' });
      }
      renderer.end();
    },
  });

  mgr.shared = { assets: Assets, audio: AudioSys, renderer, state, loop };
  const scenes = {
    title: TitleScene,
    difficultySelect: DifficultySelectScene,
    charSelect: CharSelectScene,
    game: GameScene,
    result: ResultScene,
    leaderboard: LeaderboardScene,
    musicRoom: MusicRoomScene,
    replayList: ReplayListScene,
    spellPractice: SpellPracticeScene,
    credits: CreditsScene,
    options: OptionsScene,
  };
  for (const [name, scene] of Object.entries(scenes)) {
    scene.mgr = mgr;
    mgr.register(name, scene);
  }

  document.getElementById('loading')?.classList.add('done');
  mgr.switchTo('title');
  loop.start();

  // BGM 后台激进加载：4 并发按使用顺序拉取（进关前基本已在本地；
  // 与 ensureAudio 的 in-flight 去重共享，block 点调用即自动插队）
  const BGM_QUEUE = [
    'bgm_stage1', 'bgm_boss1',
    'bgm_stage2', 'bgm_boss2',
    'bgm_stage3', 'bgm_boss3',
    'bgm_stage4', 'bgm_boss4',
    'bgm_stage5', 'bgm_boss5',
    'bgm_stage6', 'bgm_boss6',
    'bgm_ex_stage', 'bgm_boss',
    'bgm_credits',
  ];
  let qi = 0;
  const workers = Array.from({ length: 4 }, async () => {
    while (qi < BGM_QUEUE.length) {
      await Assets.ensureAudio(BGM_QUEUE[qi++]);
    }
  });
  // 首轮完成后做补漏扫描（用户定案：尽量尝试预加载任何未加载资源，
  // 失败重试；资源只增不丢，缓存交给浏览器内容指纹管理）
  Promise.all(workers).then(async () => {
    for (const k of BGM_QUEUE) {
      if (!Assets.audio(k)) await Assets.ensureAudio(k);
    }
  });

  // 调试句柄
  window.__game = { renderer, loop, mgr, Input, Assets, AudioSys, state, BF };
}

boot();
